/* eslint-disable no-console */
import { useMemo } from "react";
import { BROKER_ACTIVACION } from "../../../constants/permisos";
import {
  calcularComisionEdificiosTotal,
  calcularImporteNetoIntermediario,
  calcularPorcentajeComisionIntermediario,
  calculateComission,
  esEdificioComisionFija,
  getConvertedImporte,
  getRateForOpp,
  obtenerNombreIntermediario,
  resolverFechaFactura,
} from "../../../utils";
import { getApxMonto } from "../../../utils/apx";
import { getTakeLocal } from "../../../utils/costosIntermediacion";
import { getImporteFacturable } from "../../../utils/getImporteFacturable";
import { resolverPorcentajeBroker } from "../../../utils/resolverPorcentajeBroker";

export function usePublicidadCalcs({
  pais,
  selectedYear,
  monthsToShow,
  currentYear,
  modoCombinado,
  oportunidades,
  oportunidadesChile,
  oportunidadesPeru,
  clientes,
  clientesChile,
  clientesPeru,
  intermediarios,
  intermediariosChile,
  intermediariosPeru,
  edificios,
  edificiosChile,
  edificiosPeru,
  snapMap,
  snapMapChile,
  snapMapPeru,
  pctMap,
  pctMapChile,
  pctMapPeru,
  costosMap,
  costosMapChile,
  costosMapPeru,
  dolarFallback,
  getDolarValue,
  getDolarValueChile,
  getDolarValuePeru,
  users,
  isOportunidadesLoading,
  dolarMensualChile,
  dolarMensualPeru,
}) {
  const valoresMensuales = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);

    // Siempre calcula solo el país actual en moneda local
    const valores = Array(12).fill(0);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear) {
          valores[monthIndex] += getConvertedImporte(
            { ...opp, importe: getImporteFacturable(opp) },
            getRateForOpp(opp, getDolarValue, dolarFallback[pais])
          );
        }
      });
    return valores;
  }, [oportunidades, monthsToShow, selectedYear, isOportunidadesLoading, pais, dolarFallback, getDolarValue]);

  // Valores de Publicidad AÑO ANTERIOR (para Run Rate que cruza años)
  const valoresMensualesAnoAnterior = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    const anoAnterior = selectedYear - 1;
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        if (monthIndex >= 0 && facturaYear === anoAnterior) {
          valores[monthIndex] += getConvertedImporte(
            { ...opp, importe: getImporteFacturable(opp) },
            getRateForOpp(opp, getDolarValue, dolarFallback[pais])
          );
        }
      });
    return valores;
  }, [oportunidades, selectedYear, isOportunidadesLoading, pais, dolarFallback, getDolarValue]);

  // Valores de Publicidad CHILE (siempre en CLP, usando misma lógica)
  const valoresMensualesChile = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    oportunidadesChile
      .filter((opp) => !opp.eliminada && opp.facturafecha)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear) {
          valores[monthIndex] += getConvertedImporte(
            { ...opp, importe: getImporteFacturable(opp) },
            getRateForOpp(opp, getDolarValueChile, dolarFallback.Chile)
          );
        }
      });
    return valores;
  }, [oportunidadesChile, monthsToShow, selectedYear, isOportunidadesLoading, dolarFallback, getDolarValueChile]);

  // Valores de Publicidad PERÚ (siempre en PEN, usando misma lógica)
  const valoresMensualesPeru = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    oportunidadesPeru
      .filter((opp) => !opp.eliminada && opp.facturafecha)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear) {
          valores[monthIndex] += getConvertedImporte(
            { ...opp, importe: getImporteFacturable(opp) },
            getRateForOpp(opp, getDolarValuePeru, dolarFallback["Perú"])
          );
        }
      });
    return valores;
  }, [oportunidadesPeru, monthsToShow, selectedYear, isOportunidadesLoading, dolarFallback, getDolarValuePeru]);

  // Alias para compatibilidad (apunta al país de sesión y al otro país)
  // eslint-disable-next-line no-unused-vars
  const valoresMensualesOtroPais = pais === "Chile" ? valoresMensualesPeru : valoresMensualesChile;

  // Publicidad combinada en USD (usa valores FIJOS de Chile y Perú, no depende de sesión)
  const publicidadMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;

    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      // Chile: CLP -> USD (siempre igual independiente de sesión)
      const valorChile = valoresMensualesChile[i] || 0;
      const dolarChile = dolarMensualChile[i] || dolarFallback.Chile;
      const chileUSD = valorChile / dolarChile;

      // Perú: PEN -> USD (siempre igual independiente de sesión)
      const valorPeru = valoresMensualesPeru[i] || 0;
      const dolarPeru = dolarMensualPeru[i] || dolarFallback["Perú"];
      const peruUSD = valorPeru / dolarPeru;

      resultado[i] = chileUSD + peruUSD;
    }

    return resultado;
  }, [modoCombinado, valoresMensualesChile, valoresMensualesPeru, dolarMensualChile, dolarMensualPeru, pais]);

  const facturasPorMes = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill([]);
    const facturas = Array(12)
      .fill()
      .map(() => []);
    const oportunidadesFiltradas = oportunidades.filter(
      (opp) => !opp.eliminada && opp.facturafecha && opp.facturanumero && opp.facturanumero !== "E001-XXX"
    );
    if (oportunidadesFiltradas.length === 0) return facturas;
    const oportunidadesAgrupadas = oportunidadesFiltradas.reduce((acc, opp) => {
      const facturaDate = new Date(opp.facturafecha);
      if (isNaN(facturaDate.getTime())) {
        console.warn(`Invalid facturafecha for oportunidad ${opp.id}: ${opp.facturafecha}`);
        return acc;
      }
      const monthIndex = facturaDate.getMonth();
      const facturaYear = facturaDate.getFullYear();
      if (monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear) {
        const key = `${opp.facturanumero}-${opp.facturafecha}`;
        if (!acc[key]) {
          acc[key] = {
            facturanumero: opp.facturanumero,
            cliente: opp.cliente,
            importe: 0,
            importeOriginalMoneda: 0,
            moneda_dolar: !!opp.moneda_dolar,
            monthIndex,
          };
        }
        acc[key].importe += getConvertedImporte(
          { ...opp, importe: getImporteFacturable(opp) },
          getRateForOpp(opp, getDolarValue, dolarFallback[pais])
        );
        // #2b (7.22): la rama USD del InvoiceModal muestra importeOriginalMoneda → facturable, igual que
        // importe (rama local). Sin descuento → = importe (no-OMNET intacto).
        acc[key].importeOriginalMoneda += getImporteFacturable(opp);
        if (!opp.moneda_dolar) acc[key].moneda_dolar = false;
      }
      return acc;
    }, {}); // Inicialización explícita
    Object.values(oportunidadesAgrupadas).forEach((group) => {
      if (group && group.monthIndex !== undefined) {
        // Verificación adicional
        facturas[group.monthIndex].push({
          facturanumero: group.facturanumero,
          cliente: group.cliente || "Sin cliente",
          importe: group.importe,
          importeOriginalMoneda: group.importeOriginalMoneda,
          moneda_dolar: group.moneda_dolar,
        });
      }
    });
    return facturas;
  }, [oportunidades, monthsToShow, selectedYear, isOportunidadesLoading, dolarFallback, pais, getDolarValue]);

  // Helper: convierte opp a moneda local para cálculos de comisión
  const oppEnLocal = (opp, getDolarFn, fallback) => {
    if (!opp.moneda_dolar) return opp;
    const rate = getRateForOpp(opp, getDolarFn, fallback);
    return { ...opp, importe: (parseFloat(opp.importe) || 0) * rate, moneda_dolar: false };
  };

  const valoresMensualesComisionAgencia = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais,
            pctMap
          );
          valores[monthIndex] += getImporteFacturable(local) - neto;
        }
      });
    return valores;
  }, [oportunidades, clientes, intermediarios, monthsToShow, currentYear, pais, getDolarValue, dolarFallback, pctMap]);

  // Comisión Agencia AÑO ANTERIOR (para EBITDA Run Rate)
  const valoresMensualesComisionAgenciaAnoAnterior = useMemo(() => {
    const valores = Array(12).fill(0);
    const anoAnterior = selectedYear - 1;
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && facturaDate.getFullYear() === anoAnterior) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais,
            pctMap
          );
          valores[monthIndex] += getImporteFacturable(local) - neto;
        }
      });
    return valores;
  }, [oportunidades, clientes, intermediarios, selectedYear, pais, getDolarValue, dolarFallback, pctMap]);

  // === COMISIÓN AGENCIA FIJA CHILE ===
  const valoresMensualesComisionAgenciaChile = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesChile
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValueChile, dolarFallback.Chile);
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientesChile),
            Object.values(intermediariosChile),
            "Chile",
            pctMapChile
          );
          valores[monthIndex] += getImporteFacturable(local) - neto;
        }
      });
    return valores;
  }, [
    oportunidadesChile,
    clientesChile,
    intermediariosChile,
    selectedYear,
    getDolarValueChile,
    dolarFallback,
    pctMapChile,
  ]);

  // === COMISIÓN AGENCIA FIJA PERÚ ===
  const valoresMensualesComisionAgenciaPeru = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesPeru
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValuePeru, dolarFallback["Perú"]);
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientesPeru),
            Object.values(intermediariosPeru),
            "Perú",
            pctMapPeru
          );
          valores[monthIndex] += getImporteFacturable(local) - neto;
        }
      });
    return valores;
  }, [oportunidadesPeru, clientesPeru, intermediariosPeru, selectedYear, getDolarValuePeru, dolarFallback, pctMapPeru]);

  // ===== COMISIÓN BROKER (2ª capa de intermediación) — capa AISLADA y ADITIVA =====
  // Espejo de los memos de agencia, pero derivada de cliente.broker_id y con gate DOBLE
  // (constante dura BROKER_ACTIVACION + fecha del registro del intermediario), sobre
  // opp.facturafecha directo. No usa resolverFechaFactura (sin fallback "hoy").
  // El % de broker (post-gate) sale de la fuente única (Fase A2a). `paisCalc`/`activacion` quedan en
  // la firma para no tocar los call-sites; el helper deriva el país de `opp.pais` (=== paisCalc en
  // todos los callers, opps país-filtradas) y BROKER_ACTIVACION internamente.
  const calcBrokerMonto = (
    opp,
    facturaDate,
    clientesArr,
    intermArr,
    pctMapArg,
    paisCalc,
    activacion,
    getLocalImporte
  ) => (getLocalImporte() * resolverPorcentajeBroker(opp, clientesArr, intermArr, facturaDate, pctMapArg)) / 100;

  const valoresMensualesComisionBroker = useMemo(() => {
    const valores = Array(12).fill(0);
    const activacion = new Date(BROKER_ACTIVACION);
    const clientesArr = Object.values(clientes);
    const intermArr = Object.values(intermediarios);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          valores[monthIndex] += calcBrokerMonto(
            opp,
            facturaDate,
            clientesArr,
            intermArr,
            pctMap,
            pais,
            activacion,
            () => {
              const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
              return parseFloat(local.importe) || 0;
            }
          );
        }
      });
    return valores;
  }, [oportunidades, clientes, intermediarios, monthsToShow, selectedYear, pais, getDolarValue, dolarFallback, pctMap]);

  const valoresMensualesComisionBrokerAnoAnterior = useMemo(() => {
    const valores = Array(12).fill(0);
    const activacion = new Date(BROKER_ACTIVACION);
    const anoAnterior = selectedYear - 1;
    const clientesArr = Object.values(clientes);
    const intermArr = Object.values(intermediarios);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && facturaDate.getFullYear() === anoAnterior) {
          valores[monthIndex] += calcBrokerMonto(
            opp,
            facturaDate,
            clientesArr,
            intermArr,
            pctMap,
            pais,
            activacion,
            () => {
              const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
              return parseFloat(local.importe) || 0;
            }
          );
        }
      });
    return valores;
  }, [oportunidades, clientes, intermediarios, selectedYear, pais, getDolarValue, dolarFallback, pctMap]);

  const valoresMensualesComisionBrokerChile = useMemo(() => {
    const valores = Array(12).fill(0);
    const activacion = new Date(BROKER_ACTIVACION);
    const clientesArr = Object.values(clientesChile);
    const intermArr = Object.values(intermediariosChile);
    oportunidadesChile
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          valores[monthIndex] += calcBrokerMonto(
            opp,
            facturaDate,
            clientesArr,
            intermArr,
            pctMapChile,
            "Chile",
            activacion,
            () => {
              const local = oppEnLocal(opp, getDolarValueChile, dolarFallback.Chile);
              return parseFloat(local.importe) || 0;
            }
          );
        }
      });
    return valores;
  }, [
    oportunidadesChile,
    clientesChile,
    intermediariosChile,
    selectedYear,
    getDolarValueChile,
    dolarFallback,
    pctMapChile,
  ]);

  const valoresMensualesComisionBrokerPeru = useMemo(() => {
    const valores = Array(12).fill(0);
    const activacion = new Date(BROKER_ACTIVACION);
    const clientesArr = Object.values(clientesPeru);
    const intermArr = Object.values(intermediariosPeru);
    oportunidadesPeru
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          valores[monthIndex] += calcBrokerMonto(
            opp,
            facturaDate,
            clientesArr,
            intermArr,
            pctMapPeru,
            "Perú",
            activacion,
            () => {
              const local = oppEnLocal(opp, getDolarValuePeru, dolarFallback["Perú"]);
              return parseFloat(local.importe) || 0;
            }
          );
        }
      });
    return valores;
  }, [oportunidadesPeru, clientesPeru, intermediariosPeru, selectedYear, getDolarValuePeru, dolarFallback, pctMapPeru]);

  const comisionesAgenciaPorMes = useMemo(() => {
    const comisiones = Array(12)
      .fill()
      .map(() => []);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const importeLocal = getImporteFacturable(local);
          const neto = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais,
            pctMap
          );
          const comision = importeLocal - neto;
          const porcentaje = calcularPorcentajeComisionIntermediario(
            opp,
            Object.values(clientes),
            Object.values(intermediarios),
            pais,
            pctMap
          );
          const intermediario = obtenerNombreIntermediario(
            opp,
            Object.values(clientes),
            Object.values(intermediarios),
            pais
          );

          comisiones[monthIndex].push({
            intermediario,
            facturanumero: opp.facturanumero,
            comision,
            porcentaje,
            importe: importeLocal,
            importeOriginalMoneda: getImporteFacturable(opp),
            moneda_dolar: !!opp.moneda_dolar,
            oppId: opp.id,
          });
        }
      });
    return comisiones;
  }, [oportunidades, clientes, intermediarios, monthsToShow, selectedYear, pais, getDolarValue, dolarFallback, pctMap]);

  // Desglose BROKER por factura por mes (alimenta la sección "Broker" del modal).
  // Espejo de comisionesAgenciaPorMes, con lógica broker + gate doble. Aditivo, no toca agencia.
  const comisionesBrokerPorMes = useMemo(() => {
    const comisiones = Array(12)
      .fill()
      .map(() => []);
    const activacion = new Date(BROKER_ACTIVACION);
    const clientesArr = Object.values(clientes);
    const intermArr = Object.values(intermediarios);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const cliente = clientesArr.find((c) => c.id === opp.client_id && c.pais === pais);
          if (!cliente?.broker_id) return;
          const broker = intermArr.find((i) => i.id === parseInt(cliente.broker_id, 10) && i.pais === pais);
          if (!broker?.comision_fechainicio) return;
          // Gate doble (igual que el memo broker de Commit 1)
          if (facturaDate < activacion || facturaDate < new Date(broker.comision_fechainicio)) return;
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          // Importe facturado → facturable (7.22), espeja la sección Agencia. Base del `importe` mostrado
          // y de `comisionBroker`. Sin descuento → = importe (brokers reales intactos).
          const importeLocal = getImporteFacturable(local);
          const porcentajeBroker = broker.comision || 0;
          comisiones[monthIndex].push({
            brokerNombre: broker.nombre,
            facturanumero: opp.facturanumero,
            comisionBroker: (importeLocal * porcentajeBroker) / 100,
            porcentajeBroker,
            importe: importeLocal,
            importeOriginalMoneda: getImporteFacturable(opp),
            moneda_dolar: !!opp.moneda_dolar,
            oppId: opp.id,
          });
        }
      });
    return comisiones;
  }, [oportunidades, clientes, intermediarios, monthsToShow, selectedYear, pais, getDolarValue, dolarFallback]);

  // Desglose PROGRAMÁTICO (take del exchange + giro) por factura por mes — alimenta el grupo
  // "Programática" del modal de Costo intermediación y la suma de la línea del ER (FASE 6).
  // Espejo de comisionesBrokerPorMes; aditivo, NO toca agencia. Solo ventas `sales` con costos
  // cargados (getTakeLocal devuelve 0 si no hay fila → no se pushea). Take ya en moneda local.
  const costosProgramaticosPorMes = useMemo(() => {
    const costos = Array(12)
      .fill()
      .map(() => []);
    if (!costosMap) return costos;
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallback[pais]);
          if (!takeLocal) return;
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          costos[monthIndex].push({
            cliente: opp.cliente || "Sin cliente",
            facturanumero: opp.facturanumero,
            takeLocal,
            // Importe facturado → facturable (7.22); el take es independiente. Sin descuento → = importe.
            importe: getImporteFacturable(local),
            importeOriginalMoneda: getImporteFacturable(opp),
            moneda_dolar: !!opp.moneda_dolar,
            oppId: opp.id,
          });
        }
      });
    return costos;
  }, [oportunidades, costosMap, monthsToShow, selectedYear, pais, getDolarValue, dolarFallback]);

  // TAKE PROGRAMÁTICO — totales mensuales (local), espejo de los memos de comisión agencia.
  // Son el COSTO del take que debe entrar al agregado de costos variables (useEBITDACalcs),
  // ADEMÁS de que las comisiones ya vengan reducidas (no es doble conteo: una cosa es el costo
  // del take, otra el ahorro en comisiones). FASE 6.
  const valoresMensualesTake = useMemo(() => {
    const valores = Array(12).fill(0);
    if (!costosMap) return valores;
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          valores[monthIndex] += getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallback[pais]);
        }
      });
    return valores;
  }, [oportunidades, costosMap, monthsToShow, selectedYear, pais, getDolarValue, dolarFallback]);

  const valoresMensualesTakeAnoAnterior = useMemo(() => {
    const valores = Array(12).fill(0);
    if (!costosMap) return valores;
    const anoAnterior = selectedYear - 1;
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && facturaDate.getFullYear() === anoAnterior) {
          valores[monthIndex] += getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallback[pais]);
        }
      });
    return valores;
  }, [oportunidades, costosMap, selectedYear, pais, getDolarValue, dolarFallback]);

  const valoresMensualesTakeChile = useMemo(() => {
    const valores = Array(12).fill(0);
    if (!costosMapChile) return valores;
    oportunidadesChile
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          valores[monthIndex] += getTakeLocal(
            opp,
            "sales",
            opp.id,
            costosMapChile,
            getDolarValueChile,
            dolarFallback.Chile
          );
        }
      });
    return valores;
  }, [oportunidadesChile, costosMapChile, selectedYear, getDolarValueChile, dolarFallback]);

  const valoresMensualesTakePeru = useMemo(() => {
    const valores = Array(12).fill(0);
    if (!costosMapPeru) return valores;
    oportunidadesPeru
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          valores[monthIndex] += getTakeLocal(
            opp,
            "sales",
            opp.id,
            costosMapPeru,
            getDolarValuePeru,
            dolarFallback["Perú"]
          );
        }
      });
    return valores;
  }, [oportunidadesPeru, costosMapPeru, selectedYear, getDolarValuePeru, dolarFallback]);

  // APX (Publicis) — desglose por factura por mes para el grupo "APX" del modal + suma de la línea
  // "Costo intermediación". Espejo de costosProgramaticosPorMes. Solo ventas con apx_id (getApxMonto
  // devuelve 0 si null → no se pushea). APX = importe × % en moneda local. Etapa 2 (inerte hoy).
  const apxPorMes = useMemo(() => {
    const filas = Array(12)
      .fill()
      .map(() => []);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const apxLocal = getApxMonto(local, intermediarios, pctMap);
          if (!apxLocal) return;
          const apxId = parseInt(opp.apx_id, 10);
          const inter = Object.values(intermediarios).find((i) => i.id === apxId);
          filas[monthIndex].push({
            cliente: opp.cliente || "Sin cliente",
            facturanumero: opp.facturanumero,
            apxLocal,
            porcentaje: inter?.comision || 0,
            // Importe facturado → facturable (7.22); apxLocal es independiente. Sin descuento → = importe.
            importe: getImporteFacturable(local),
            importeOriginalMoneda: getImporteFacturable(opp),
            moneda_dolar: !!opp.moneda_dolar,
            oppId: opp.id,
          });
        }
      });
    return filas;
  }, [oportunidades, intermediarios, monthsToShow, selectedYear, pais, getDolarValue, dolarFallback, pctMap]);

  // APX — totales mensuales (local), espejo de valoresMensualesTake*. COSTO que entra a los 4
  // agregados del ER, ADEMÁS de reducir la base de comisión. Etapa 2 (inerte: apx_id null → 0).
  const valoresMensualesApx = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          valores[monthIndex] += getApxMonto(local, intermediarios, pctMap);
        }
      });
    return valores;
  }, [oportunidades, intermediarios, monthsToShow, selectedYear, pais, getDolarValue, dolarFallback, pctMap]);

  const valoresMensualesApxAnoAnterior = useMemo(() => {
    const valores = Array(12).fill(0);
    const anoAnterior = selectedYear - 1;
    oportunidades
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && facturaDate.getFullYear() === anoAnterior) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          valores[monthIndex] += getApxMonto(local, intermediarios, pctMap);
        }
      });
    return valores;
  }, [oportunidades, intermediarios, selectedYear, pais, getDolarValue, dolarFallback, pctMap]);

  const valoresMensualesApxChile = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesChile
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValueChile, dolarFallback.Chile);
          valores[monthIndex] += getApxMonto(local, intermediariosChile, pctMapChile);
        }
      });
    return valores;
  }, [oportunidadesChile, intermediariosChile, selectedYear, getDolarValueChile, dolarFallback, pctMapChile]);

  const valoresMensualesApxPeru = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesPeru
      .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValuePeru, dolarFallback["Perú"]);
          valores[monthIndex] += getApxMonto(local, intermediariosPeru, pctMapPeru);
        }
      });
    return valores;
  }, [oportunidadesPeru, intermediariosPeru, selectedYear, getDolarValuePeru, dolarFallback, pctMapPeru]);

  const valoresMensualesComisiones = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    oportunidades
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais,
            pctMap
          );
          const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallback[pais]);
          const apxLocal = getApxMonto(local, intermediarios, pctMap);
          valores[monthIndex] += calcularComisionEdificiosTotal(
            local,
            edificios,
            netoFactura,
            snapMap,
            takeLocal,
            apxLocal
          );
        }
      });
    return valores;
  }, [
    oportunidades,
    edificios,
    snapMap,
    pctMap,
    costosMap,
    clientes,
    intermediarios,
    pais,
    monthsToShow,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
  ]);

  // Comisión Edificios AÑO ANTERIOR (para EBITDA Run Rate)
  const valoresMensualesComisionesAnoAnterior = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    const anoAnterior = selectedYear - 1;
    oportunidades
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && facturaDate.getFullYear() === anoAnterior) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais,
            pctMap
          );
          const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallback[pais]);
          const apxLocal = getApxMonto(local, intermediarios, pctMap);
          valores[monthIndex] += calcularComisionEdificiosTotal(
            local,
            edificios,
            netoFactura,
            snapMap,
            takeLocal,
            apxLocal
          );
        }
      });
    return valores;
  }, [
    oportunidades,
    edificios,
    snapMap,
    pctMap,
    costosMap,
    clientes,
    intermediarios,
    pais,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
  ]);

  // === COMISIÓN EDIFICIOS FIJA CHILE ===
  const valoresMensualesComisionesChile = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesChile
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValueChile, dolarFallback.Chile);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientesChile),
            Object.values(intermediariosChile),
            "Chile",
            pctMapChile
          );
          const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMapChile, getDolarValueChile, dolarFallback.Chile);
          const apxLocal = getApxMonto(local, intermediariosChile, pctMapChile);
          valores[monthIndex] += calcularComisionEdificiosTotal(
            local,
            edificiosChile,
            netoFactura,
            snapMapChile,
            takeLocal,
            apxLocal
          );
        }
      });
    return valores;
  }, [
    oportunidadesChile,
    edificiosChile,
    snapMapChile,
    pctMapChile,
    costosMapChile,
    clientesChile,
    intermediariosChile,
    selectedYear,
    getDolarValueChile,
    dolarFallback,
  ]);

  // === COMISIÓN EDIFICIOS FIJA PERÚ ===
  const valoresMensualesComisionesPeru = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesPeru
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < 12 && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValuePeru, dolarFallback["Perú"]);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientesPeru),
            Object.values(intermediariosPeru),
            "Perú",
            pctMapPeru
          );
          const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMapPeru, getDolarValuePeru, dolarFallback["Perú"]);
          const apxLocal = getApxMonto(local, intermediariosPeru, pctMapPeru);
          valores[monthIndex] += calcularComisionEdificiosTotal(
            local,
            edificiosPeru,
            netoFactura,
            snapMapPeru,
            takeLocal,
            apxLocal
          );
        }
      });
    return valores;
  }, [
    oportunidadesPeru,
    edificiosPeru,
    snapMapPeru,
    pctMapPeru,
    costosMapPeru,
    clientesPeru,
    intermediariosPeru,
    selectedYear,
    getDolarValuePeru,
    dolarFallback,
  ]);

  const comisionesEdificiosPorMes = useMemo(() => {
    if (isOportunidadesLoading) {
      return Array(12)
        .fill([])
        .map(() => []);
    }
    const comisiones = Array(12)
      .fill([])
      .map(() => []);
    oportunidades
      .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return;
        const monthIndex = facturaDate.getMonth();
        if (monthIndex >= 0 && monthIndex < monthsToShow && facturaDate.getFullYear() === selectedYear) {
          const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
          const netoFactura = calcularImporteNetoIntermediario(
            local,
            Object.values(clientes),
            Object.values(intermediarios),
            pais,
            pctMap
          );
          const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallback[pais]);
          const apxLocal = getApxMonto(local, intermediarios, pctMap);
          const totalComision = calcularComisionEdificiosTotal(
            local,
            edificios,
            netoFactura,
            snapMap,
            takeLocal,
            apxLocal
          );
          const cliente = opp.cliente || "Sin cliente";
          // Detectar si todos los edificios participantes son fijos (para mostrar etiqueta en modal)
          const fecha = resolverFechaFactura(opp);
          const edIds = opp.edificiosparticipantes
            ? [...new Set(opp.edificiosparticipantes.split(",").map(Number))]
            : [];
          const soloFijos =
            edIds.length > 0 &&
            edIds.every((id) => {
              const ed = edificios[id];
              return ed && esEdificioComisionFija(ed, fecha);
            });
          comisiones[monthIndex].push({
            facturanumero: opp.facturanumero,
            cliente,
            totalComision,
            oppId: opp.id,
            soloFijos,
            sinEdificios: edIds.length === 0,
          });
        }
      });
    return comisiones;
  }, [
    oportunidades,
    edificios,
    snapMap,
    pctMap,
    costosMap,
    clientes,
    intermediarios,
    pais,
    monthsToShow,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
  ]);

  const valoresMensualesComisionComercial = useMemo(() => {
    if (isOportunidadesLoading)
      return {
        valores: Array(12).fill(0),
        comisionesPorOportunidad: {},
      };
    const valores = Array(12).fill(0);
    const comisionesPorOportunidad = {};

    oportunidades
      .filter((opp) => {
        if (opp.estatus !== "100%" || opp.eliminada || !opp.facturafecha) return false;
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return false;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        return monthIndex >= 0 && monthIndex < monthsToShow && facturaYear === selectedYear;
      })
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        const monthIndex = facturaDate.getMonth();
        const oppId = opp.id;
        const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
        // Importe facturado → facturable (descuento de acuerdo, 7.22). Alimenta la columna "Importe
        // Facturado" (rama local) y la base del neto (`neto` más abajo). Sin descuento → = importe.
        const importeLocal = getImporteFacturable(local);
        const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallback[pais]);
        const apxLocal = getApxMonto(local, intermediarios, pctMap);
        const comisionComercial = calculateComission.calculateSalesCommission(
          local,
          clientes,
          intermediarios,
          edificios,
          undefined,
          snapMap,
          takeLocal,
          pctMap
        );
        const comisionAgencia = calculateComission.calculateAgencyCommission(
          local,
          clientes,
          intermediarios,
          undefined,
          pctMap
        );
        const netoAgencia = calcularImporteNetoIntermediario(
          local,
          Object.values(clientes),
          Object.values(intermediarios),
          pais,
          pctMap
        );
        const comisionEdificios = calcularComisionEdificiosTotal(
          local,
          edificios,
          netoAgencia,
          snapMap,
          takeLocal,
          apxLocal
        );
        comisionesPorOportunidad[oppId] = {
          facturanumero: opp.facturanumero,
          importe: importeLocal,
          importeOriginalMoneda: getImporteFacturable(opp),
          moneda_dolar: !!opp.moneda_dolar,
          ejecutivo: opp.ejecutivo ? users[opp.ejecutivo] || "Sin Ejecutivo" : "Sin Ejecutivo",
          comisionComercial,
          comisionAgencia,
          comisionEdificios,
          neto: importeLocal - comisionAgencia - comisionEdificios - takeLocal - apxLocal,
          desglose: calculateComission.getSalesCommissionBreakdown(
            local,
            clientes,
            intermediarios,
            edificios,
            undefined,
            snapMap,
            takeLocal
          ),
        };
        valores[monthIndex] += comisionComercial;
      });

    return { valores, comisionesPorOportunidad };
  }, [
    oportunidades,
    clientes,
    intermediarios,
    edificios,
    snapMap,
    pctMap,
    costosMap,
    users,
    monthsToShow,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
    pais,
  ]);

  // Comisión Comercial AÑO ANTERIOR (para EBITDA Run Rate)
  const valoresMensualesComisionComercialAnoAnterior = useMemo(() => {
    if (isOportunidadesLoading) return Array(12).fill(0);
    const valores = Array(12).fill(0);
    const anoAnterior = selectedYear - 1;

    oportunidades
      .filter((opp) => {
        if (opp.estatus !== "100%" || opp.eliminada || !opp.facturafecha) return false;
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return false;
        const facturaYear = facturaDate.getFullYear();
        return facturaYear === anoAnterior;
      })
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        const monthIndex = facturaDate.getMonth();
        const local = oppEnLocal(opp, getDolarValue, dolarFallback[pais]);
        const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallback[pais]);
        valores[monthIndex] += calculateComission.calculateSalesCommission(
          local,
          clientes,
          intermediarios,
          edificios,
          undefined,
          snapMap,
          takeLocal
        );
      });

    return valores;
  }, [
    oportunidades,
    clientes,
    intermediarios,
    edificios,
    snapMap,
    costosMap,
    selectedYear,
    isOportunidadesLoading,
    getDolarValue,
    dolarFallback,
    pais,
  ]);

  // === COMISIÓN COMERCIAL FIJA CHILE ===
  const valoresMensualesComisionComercialChile = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesChile
      .filter((opp) => {
        if (opp.estatus !== "100%" || opp.eliminada || !opp.facturafecha) return false;
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return false;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        return monthIndex >= 0 && monthIndex < 12 && facturaYear === selectedYear;
      })
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        const monthIndex = facturaDate.getMonth();
        const local = oppEnLocal(opp, getDolarValueChile, dolarFallback.Chile);
        const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMapChile, getDolarValueChile, dolarFallback.Chile);
        valores[monthIndex] += calculateComission.calculateSalesCommission(
          local,
          clientesChile,
          intermediariosChile,
          edificiosChile,
          undefined,
          snapMapChile,
          takeLocal
        );
      });
    return valores;
  }, [
    oportunidadesChile,
    clientesChile,
    intermediariosChile,
    edificiosChile,
    snapMapChile,
    costosMapChile,
    selectedYear,
    getDolarValueChile,
    dolarFallback,
  ]);

  // === COMISIÓN COMERCIAL FIJA PERÚ ===
  const valoresMensualesComisionComercialPeru = useMemo(() => {
    const valores = Array(12).fill(0);
    oportunidadesPeru
      .filter((opp) => {
        if (opp.estatus !== "100%" || opp.eliminada || !opp.facturafecha) return false;
        const facturaDate = new Date(opp.facturafecha);
        if (isNaN(facturaDate.getTime())) return false;
        const monthIndex = facturaDate.getMonth();
        const facturaYear = facturaDate.getFullYear();
        return monthIndex >= 0 && monthIndex < 12 && facturaYear === selectedYear;
      })
      .forEach((opp) => {
        const facturaDate = new Date(opp.facturafecha);
        const monthIndex = facturaDate.getMonth();
        const local = oppEnLocal(opp, getDolarValuePeru, dolarFallback["Perú"]);
        const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMapPeru, getDolarValuePeru, dolarFallback["Perú"]);
        valores[monthIndex] += calculateComission.calculateSalesCommission(
          local,
          clientesPeru,
          intermediariosPeru,
          edificiosPeru,
          undefined,
          snapMapPeru,
          takeLocal
        );
      });
    return valores;
  }, [
    oportunidadesPeru,
    clientesPeru,
    intermediariosPeru,
    edificiosPeru,
    snapMapPeru,
    costosMapPeru,
    selectedYear,
    getDolarValuePeru,
    dolarFallback,
  ]);

  const ytdComisionAgencia = useMemo(
    () => valoresMensualesComisionAgencia.slice(0, monthsToShow).reduce((acc, val) => acc + val, 0),
    [valoresMensualesComisionAgencia, monthsToShow]
  );

  const ytdComisiones = useMemo(
    () => valoresMensualesComisiones.slice(0, monthsToShow).reduce((acc, val) => acc + val, 0),
    [valoresMensualesComisiones, monthsToShow]
  );

  const ytdComisionComercial = useMemo(
    () => valoresMensualesComisionComercial.valores.slice(0, monthsToShow).reduce((acc, val) => acc + val, 0),
    [valoresMensualesComisionComercial, monthsToShow]
  );

  return {
    valoresMensuales,
    valoresMensualesAnoAnterior,
    valoresMensualesChile,
    valoresMensualesPeru,
    valoresMensualesOtroPais,
    publicidadMensualesCombinados,
    facturasPorMes,
    valoresMensualesComisionAgencia,
    valoresMensualesComisionAgenciaAnoAnterior,
    valoresMensualesComisionAgenciaChile,
    valoresMensualesComisionAgenciaPeru,
    valoresMensualesComisionBroker,
    valoresMensualesComisionBrokerAnoAnterior,
    valoresMensualesComisionBrokerChile,
    valoresMensualesComisionBrokerPeru,
    comisionesBrokerPorMes,
    comisionesAgenciaPorMes,
    costosProgramaticosPorMes,
    valoresMensualesTake,
    valoresMensualesTakeAnoAnterior,
    valoresMensualesTakeChile,
    valoresMensualesTakePeru,
    apxPorMes,
    valoresMensualesApx,
    valoresMensualesApxAnoAnterior,
    valoresMensualesApxChile,
    valoresMensualesApxPeru,
    valoresMensualesComisiones,
    valoresMensualesComisionesAnoAnterior,
    valoresMensualesComisionesChile,
    valoresMensualesComisionesPeru,
    comisionesEdificiosPorMes,
    valoresMensualesComisionComercial,
    valoresMensualesComisionComercialAnoAnterior,
    valoresMensualesComisionComercialChile,
    valoresMensualesComisionComercialPeru,
    ytdComisionAgencia,
    ytdComisiones,
    ytdComisionComercial,
  };
}
